import React from 'react';

import useTabs from './useTabs';

interface TabItem {
  id: string;
  title: string;
  content: React.ReactNode;
  disabled?: boolean;
  dataCy?: string;
}

interface TabsProps {
  tabs: TabItem[];
  queryKey?: string; // Sync with URL query parameter
  contentContainerStyle?: string; // For custom height and width
  activeTabId?: string; // Optional: for controlled mode
  dataCy?: string;
  onTabChange?: (tab: TabItem) => void; // Optional: for controlled mode
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  queryKey,
  contentContainerStyle,
  activeTabId,
  dataCy,
  onTabChange,
}) => {
  const internal = useTabs(tabs, queryKey);

  const activeTabIdResolved = activeTabId || internal.currentTab?.id;
  const currentTab = tabs.find((t) => t.id === activeTabIdResolved) ?? tabs[0];

  const handleTabChange = (tab: TabItem) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      internal.handleTabChange(tab);
    }
  };

  const getTabStateClassName = (tab: TabItem): string => {
    if (tab.disabled) {
      return 'text-gray-400 cursor-not-allowed';
    }
    if (currentTab?.id === tab.id) {
      return 'text-blue-600 border-b-2 border-blue-600';
    }
    return 'text-gray-500 dark:text-gray-300 hover:text-gray-700 hover:border-gray-300';
  };

  return (
    <div className="tabs-component mt-5 hide-scrollbar">
      {/* Tab Headers — horizontally scrollable on narrow screens so many tabs
          don't wrap or cramp; buttons keep their width (shrink-0) and the row
          scrolls instead. */}
      <div
        data-cy={dataCy ? `${dataCy}-buttons` : undefined}
        className="tabs-header flex overflow-x-auto hide-scrollbar border-b border-gray-300 dark:border-dark-borderHeavy"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabChange(tab)}
            disabled={tab.disabled}
            data-cy={tab.dataCy}
            // The active tab is otherwise only a colour class; this exposes
            // the same state without tying a test to the styling.
            data-state={currentTab?.id === tab.id ? 'active' : 'inactive'}
            className={`tab-item shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium ${getTabStateClassName(
              tab,
            )}`}
          >
            {tab.title}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        className={`tabs-content hide-scrollbar my-5 ${
          contentContainerStyle || 'overflow-x-auto'
        }`}
      >
        {currentTab?.content}
      </div>
    </div>
  );
};

export default Tabs;
